import React from 'react';

/**
 * Reusable Button.
 *
 * Props (backward compatible):
 * - variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success'
 * - size:    'xs' | 'sm' | 'md' | 'lg' | 'icon'
 * - loading: boolean — shows spinner and disables
 * - leftIcon / rightIcon: ReactNode icons rendered alongside children
 * - block:   boolean — stretches to full width
 */
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  block = false,
  type = 'button',
  ...props
}) => {
  const baseStyles =
    'group inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg ' +
    'transition-all duration-200 ease-swift focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed select-none whitespace-nowrap';

  const variants = {
    primary:
      'bg-gradient-to-r from-primary-600 to-primary-700 text-white ' +
      'hover:from-primary-700 hover:to-primary-800 focus-visible:ring-primary-500 ' +
      'shadow-md hover:shadow-glow-primary active:scale-[0.98]',
    secondary:
      'bg-slate-100 text-slate-800 hover:bg-slate-200 focus-visible:ring-slate-400 ' +
      'border border-slate-200/80',
    outline:
      'border-2 border-primary-600 text-primary-700 hover:bg-primary-50 ' +
      'focus-visible:ring-primary-500 bg-white/80',
    ghost:
      'text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-400',
    danger:
      'bg-gradient-to-r from-rose-600 to-rose-700 text-white ' +
      'hover:from-rose-700 hover:to-rose-800 focus-visible:ring-rose-500 ' +
      'shadow-md hover:shadow-lg active:scale-[0.98]',
    success:
      'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white ' +
      'hover:from-emerald-700 hover:to-emerald-800 focus-visible:ring-emerald-500 ' +
      'shadow-md hover:shadow-lg active:scale-[0.98]',
  };

  const sizes = {
    xs: 'px-2 py-1.5 text-xs rounded-md',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3.5 py-2 text-sm',
    lg: 'px-4 py-2.5 text-sm',
    icon: 'h-10 w-10 p-0',
  };

  return (
    <button
      type={type}
      className={`${baseStyles} ${variants[variant] || variants.primary} ${sizes[size] || sizes.md} ${block ? 'w-full' : ''} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {!loading && leftIcon && <span className="inline-flex shrink-0">{leftIcon}</span>}
      {children}
      {!loading && rightIcon && <span className="inline-flex shrink-0">{rightIcon}</span>}
    </button>
  );
};

export default Button;
